import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@car/ui/components/alert-dialog";
import { Badge } from "@car/ui/components/badge";
import { Button } from "@car/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@car/ui/components/card";
import { Image } from "@car/ui/components/image";
import { Skeleton } from "@car/ui/components/skeleton";
import { GaugeIcon, Shield01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";

import {
  PageHeader,
  NoVehicleEmpty,
  PanelQueryError,
  Plate,
  WarrantyBadge,
} from "@/components/panel-bits";
import { faDate, faKm, faPlainNumber } from "@/lib/format";
import { garageName, type GarageVehicle } from "@/lib/panel";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/panel/vehicles/")({
  head: () => ({ meta: [{ title: "خودروهای من | پنل مشتریان تابان خودرو" }] }),
  component: MyVehiclesPage,
});

type VehicleAccess = { canBook: boolean; ownerName: string };

function MyVehiclesPage() {
  const query = useQuery(orpc.panel.garage.list.queryOptions());

  return (
    <div className="grid gap-6">
      <PageHeader
        title="خودروهای من"
        description="مشخصات مالکیت، گارانتی و برنامه سرویس هر خودرو."
      />

      <IncomingTransfers />

      {query.isPending ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-72 rounded-xl" />
          <Skeleton className="h-72 rounded-xl" />
        </div>
      ) : query.isError ? (
        <PanelQueryError title="خودروهای شما بارگیری نشد" query={query} />
      ) : (
        <>
          {query.data.garage.length === 0 ? (
            <NoVehicleEmpty />
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              {query.data.garage.map((item) => (
                <VehicleCard key={item.owned.id} item={item} access={null} />
              ))}
            </div>
          )}
          {query.data.shared.length > 0 ? (
            <section aria-labelledby="shared-title" className="grid gap-3">
              <h2 id="shared-title" className="text-lg font-black">
                خودروهای مشترک با شما
              </h2>
              <div className="grid gap-4 lg:grid-cols-2">
                {query.data.shared.map((item) => (
                  <VehicleCard key={item.owned.id} item={item} access={item.access} />
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

function VehicleCard({ item, access }: { item: GarageVehicle; access: VehicleAccess | null }) {
  return (
    <Card className={item.vehicle.image ? "overflow-hidden pt-0" : ""}>
      {item.vehicle.image ? (
        <Image
          src={item.vehicle.image}
          alt={item.vehicle.name}
          width={960}
          height={264}
          sizes="(max-width: 1024px) 100vw, 50vw"
          className="h-44 w-full object-cover"
        />
      ) : null}
      <CardHeader className="pb-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">
            {garageName(item)}{" "}
            <span className="font-normal text-muted-foreground">
              مدل {faPlainNumber(item.owned.modelYear)}
            </span>
          </CardTitle>
          {access ? (
            <Badge variant="outline">
              مشترک از طرف {access.ownerName || "مالک"}
              {access.canBook ? "، با اجازه رزرو" : "، فقط مشاهده"}
            </Badge>
          ) : (
            <WarrantyBadge warranty={item.warranty} />
          )}
        </div>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Plate plate={item.owned.plate} text={item.owned.plateText} />
          {access ? null : (
            <span className="text-sm text-muted-foreground">
              خرید {faDate(item.owned.purchaseDateIso)}
            </span>
          )}
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <HugeiconsIcon icon={GaugeIcon} strokeWidth={1.8} className="size-4" />
            <dt>کارکرد تخمینی</dt>
          </div>
          <dd className="text-start font-bold">{faKm(item.estimatedKm)}</dd>
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <HugeiconsIcon icon={Shield01Icon} strokeWidth={1.8} className="size-4" />
            <dt>پایان گارانتی</dt>
          </div>
          <dd className="text-start font-bold">{faDate(item.warranty.endDateIso)}</dd>
        </dl>
        <p className="-mt-2 text-xs text-muted-foreground">{item.estimateNote}</p>
        {item.nextDue ? (
          <p className="rounded-lg bg-muted/60 p-3 text-sm">
            <span className="font-bold">سرویس بعدی:</span> {item.nextDue.item.title}،{" "}
            {item.nextDue.text}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            nativeButton={false}
            render={<Link to="/panel/vehicles/$ownedId" params={{ ownedId: item.owned.id }} />}
          >
            {access ? "جزئیات خودرو" : "جزئیات و سوابق"}
          </Button>
          {!access || access.canBook ? (
            <Button
              size="sm"
              variant="outline"
              nativeButton={false}
              render={<Link to="/panel/appointments/new" search={{ vehicle: item.owned.id }} />}
            >
              رزرو نوبت برای این خودرو
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/** خودروهایی که فروشنده به شماره شما منتقل کرده و منتظر پذیرش شماست */
function IncomingTransfers() {
  const queryClient = useQueryClient();
  const query = useQuery(orpc.panel.sharing.incoming.queryOptions());
  const onDone = (message: string) => (result: { ok: boolean; reason: string }) => {
    if (!result.ok) {
      toast.error(result.reason);
      return;
    }
    toast.success(message);
    queryClient.invalidateQueries();
  };
  const accept = useMutation(
    orpc.panel.sharing.acceptTransfer.mutationOptions({
      onSuccess: onDone("خودرو به خودروهای شما اضافه شد"),
      onError: () => toast.error("پذیرش انجام نشد؛ دوباره تلاش کنید."),
    }),
  );
  const decline = useMutation(
    orpc.panel.sharing.declineTransfer.mutationOptions({
      onSuccess: onDone("انتقال رد شد"),
      onError: () => toast.error("انجام نشد؛ دوباره تلاش کنید."),
    }),
  );
  const transfers = query.data?.transfers ?? [];
  if (transfers.length === 0) return null;

  return (
    <section aria-label="انتقال‌های در انتظار پذیرش" className="grid gap-3">
      {transfers.map((transfer) => (
        <Card key={transfer.id} className="border-primary/40 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-base">انتقال {transfer.vehicleName} به نام شما</CardTitle>
            <CardDescription>
              {transfer.fromName || "مالک خودرو"} در {faDate(transfer.createdIso)} این خودرو را به
              شماره شما منتقل کرد. با پذیرش، برنامه سرویس و سوابق فنی خودرو به پنل شما می‌آید؛ مدارک
              شخصی و فاکتورهای مالک قبلی منتقل نمی‌شود.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <AlertDialog>
              <AlertDialogTrigger render={<Button disabled={accept.isPending} />}>
                پذیرش خودرو
              </AlertDialogTrigger>
              <AlertDialogContent lang="fa" dir="rtl">
                <AlertDialogHeader>
                  <AlertDialogTitle>{transfer.vehicleName} را می‌پذیرید؟</AlertDialogTitle>
                  <AlertDialogDescription>
                    خودرو از پنل فروشنده برداشته و به خودروهای شما اضافه می‌شود. کارت خودرو و
                    بیمه‌نامه را پس از پذیرش به نام خودتان ثبت کنید.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>انصراف</AlertDialogCancel>
                  <AlertDialogAction onClick={() => accept.mutate({ transferId: transfer.id })}>
                    بله، می‌پذیرم
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              variant="outline"
              disabled={decline.isPending}
              onClick={() => decline.mutate({ transferId: transfer.id })}
            >
              این خودرو مال من نیست
            </Button>
          </CardContent>
        </Card>
      ))}
    </section>
  );
}
